import React, { useState, useEffect } from "react";
import _ from "lodash";
import { useSelector, useDispatch } from "react-redux";
import { Button, Menu, Container } from "semantic-ui-react";
import { Link, useNavigate } from "react-router-dom";
import { ApiRequest } from "../../firebase/server";
import { changeStatus, createBanner } from "../../firebase";
import {
  fetchOrders,
  fetchLineItems,
  openModal,
  closeModal,
  fetchAdRequest,
} from "../../actions";
import { checkUser } from "./helpers";
//import { OrderData, LineItemData } from "../../data/ad-data";

const EditButton = ({ id, inUse, size }) => {
  const [showEdit, setShowEdit] = useState(false);

  const retailer = useSelector((state) => state.retailer);
  const user = useSelector((state) => state.user);
  const advertiser = useSelector((state) => state.currentAdvertiser);

  useEffect(() => {
    if (id && checkUser(user, advertiser, retailer)) setShowEdit(true);
  }, [id, user, retailer, advertiser]);

  return (
    showEdit && (
      <Button
        as={Link}
        to="edit"
        primary
        disabled={inUse ? true : false}
        size={size ? size : "small"}
      >
        Edit
      </Button>
    )
  );
};

const CancelButton = ({ location, id, retailer, pristine, size }) => {
  const navigate = useNavigate();
  const page = useSelector((state) => state.page);
  const editLocation = `/${retailer}/banner/${id}/edit`;
  const path = location.pathname;
  const handleClick = () => {
    if (path === editLocation) {
      if (!pristine) {
        const conf = window.confirm(
          "You are about to leave this page. Any changes you have made will be lost. Click ok to continue or cancel to stay on the page and save your changes."
        );
        if (conf) {
          navigate(`/${retailer}/banner/${id}`);
          changeStatus(id, false);
        }
      } else {
        navigate(`/${retailer}/banner/${id}`);
        changeStatus(id, false);
      }
    } else {
      let p = page ? page : "1";
      navigate(`/${retailer}/list/${p}`);
    }
  };
  return (
    <Button onClick={handleClick} type="button" size={size ? size : "small"}>
      {path === editLocation ? "Preview" : "Back to list"}
    </Button>
  );
};

const DuplicateButton = ({ data, inUse, size }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [showDuplicate, setShowDuplicate] = useState(false);

  const retailer = useSelector((state) => state.retailer);
  const user = useSelector((state) => state.user);
  const advertiser = useSelector((state) => state.currentAdvertiser);

  useEffect(() => {
    if (checkUser(user, advertiser, retailer)) setShowDuplicate(true);
  }, [user, retailer, advertiser]);

  const handleClick = () => {
    const conf = window.confirm(
      `You are about to duplicate ${data.bannerTitle}. Please confirm this action`
    );
    if (conf) {
      setLoading(true);
      let banner = _.omit(data, ["key", "adServeData"]);
      banner = { ...banner, bannerTitle: `${banner.bannerTitle} DUPLICATE` };
      createBanner(banner).then((message) => {
        navigate(`/${banner.retailer}/banner/${message.id}/edit`);
      });
    }
  };
  return (
    showDuplicate && (
      <Button
        positive
        onClick={handleClick}
        loading={loading}
        disabled={inUse ? true : false}
        size={size ? size : "small"}
        type="button"
      >
        Duplicate
      </Button>
    )
  );
};

const GamButtons = ({
  banner: {
    advertiserId,
    adNumber,
    retailer,
    adServeData,
    lastModAt,
    bannerTitle,
    advertiser,
  },
  service,
}) => {
  const dispatch = useDispatch();
  const banner = useSelector((state) => state.banner);
  const user = useSelector((state) => state.user);

  const [showGamButtons, setShowGamButtons] = useState(false);

  useEffect(() => {
    if (checkUser(user, advertiser, retailer)) setShowGamButtons(true);
  }, [user, retailer, advertiser]);

  const serverRequest = ({ call }) => {
    const message =
      call === "getOrders"
        ? `Finding orders for ${advertiser}`
        : `Finding line items for Ad Number ${adNumber}`;
    dispatch(openModal("LoadingModal", { centered: false, title: message }));
    const request = { retailer, service, call, advertiserId, adNumber };
    dispatch(fetchAdRequest(request));
    ApiRequest(request).then(
      (results) => {
        if (call === "getOrders") {
          dispatch(fetchOrders(results.result.rval.results));
        } else {
          dispatch(fetchLineItems(results.result.rval.results));
        }
        dispatch(openModal("AdDataModal", { centered: false }));
      },
      (error) => {
        dispatch(
          openModal("ErrorModal", {
            centered: false,
            type: "negative",
            error: {
              name: message,
              error: `Google Ad Manager error: ${error.message}`,
            },
          })
        );
      }
    );
  };

  const uploadEdit = async () => {
    dispatch(
      openModal("LoadingModal", {
        centered: false,
        title: `Uploading ${banner.bannerTitle}`,
      })
    );
    const request = {
      retailer: banner.retailer,
      service: "gam",
      call: "editCreative",
      banner,
    };
    ApiRequest(request).then(
      (results) => {
        dispatch(closeModal());
      },
      (error) => {
        console.log(error);
      }
    );
  };

  return (
    showGamButtons && (
      <Menu fixed="bottom" size="mini" secondary>
        {!adServeData && (
          <Container>
            <Menu.Item>
              <Button
                positive
                type="button"
                size="mini"
                onClick={() =>
                  serverRequest({
                    call: "getOrders",
                  })
                }
              >
                Find orders
              </Button>
            </Menu.Item>
            <Menu.Item>
              <Button
                positive
                type="button"
                size="mini"
                onClick={() =>
                  serverRequest({
                    call: "getLineItems",
                  })
                }
              >
                Find line items
              </Button>
            </Menu.Item>
          </Container>
        )}
        {adServeData && (
          <Container>
            <Menu.Item>
              <Button
                positive
                type="button"
                size="mini"
                disabled={
                  adServeData[0].lastModAt.seconds === lastModAt.seconds
                }
                onClick={() =>
                  uploadEdit({
                    call: "editCreative",
                  })
                }
              >
                Upload edit
              </Button>
            </Menu.Item>
          </Container>
        )}
      </Menu>
    )
  );
};

const KevelButtons = ({ banner, template }) => {
  const dispatch = useDispatch();
  const { fields } = template;

  const uploadImages = async (creativeId) => {
    for (const field of fields) {
      if (
        field.type === "File" &&
        banner.fields[field.variable] !== undefined
      ) {
        await ApiRequest({
          retailer: banner.retailer,
          service: "kevel",
          call: "uploadCreativeImage",
          creativeId,
          field: field.variable,
          imageUrl: banner.fields[field.variable],
        });
      }
    }
  };

  const uploadBanner = (call) => {
    //open loading modal
    dispatch(
      openModal("LoadingModal", {
        centered: false,
        title: `Loading ${banner.bannerTitle}`,
      })
    );

    //create the upload object
    const creative = {
      title: banner.bannerTitle,
      isActive: true,
      advertiserId: banner.advertiserId,
      adTypeId: banner.adTypeId,
      url: banner.url,
      templateId: banner.templateId,
      templateValues: JSON.stringify(banner.fields),
      key: banner.key,
      lastModAt: banner.lastModAt,
    };

    if (banner.adServeData) {
      creative.id = banner.adServeData.creativeId;
    }

    //call the api
    const request = {
      retailer: banner.retailer,
      service: "kevel",
      call,
      banner: creative,
    };

    ApiRequest(request)
      .then(({ result }) => {
        return result;
      })
      .then(async (result) => {
        //upload images to creative id
        await uploadImages(result.id);
        return result;
      })
      .then((result) => {
        dispatch(
          openModal("MessageModal", {
            centered: false,
            title: `Loading ${banner.bannerTitle}`,
            message: "Upload complete",
            color: "green",
          })
        );
      })
      .catch((error) => {
        console.log("ApiRequest error", error);
      });
  };

  return (
    <Menu fixed="bottom" size="mini" secondary>
      <Container>
        <Menu.Item>
          {banner.adServeData ? (
            <Button
              positive
              type="button"
              size="mini"
              disabled={
                banner.adServeData.lastModAt.seconds ===
                banner.lastModAt.seconds
              }
              onClick={() => uploadBanner("editCreative")}
            >
              Update banner
            </Button>
          ) : (
            <Button
              positive
              type="button"
              size="mini"
              onClick={() => uploadBanner("uploadCreative")}
            >
              Upload banner
            </Button>
          )}
        </Menu.Item>
      </Container>
    </Menu>
  );
};

export { EditButton, CancelButton, DuplicateButton, GamButtons, KevelButtons };
